import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  styled,
} from '@mui/material'
import { intl } from '../../../../../i18n'
import { DialogButton } from '../../../../components/dialogs'
import { ProjectForSelection } from '../../programManagement'
import { fetchAllProjects } from '../../../../../infrastructure/api/project'
import TextField from '@mui/material/TextField'
import { colorPalette } from '../../../../style/colorPalette'

type ProjectSelectionDialogProps = {
  open: boolean
  onClose: () => void
  onSelect: (projects: ProjectForSelection[]) => void
  existingProjectUuids: string[]
}

export const ProjectSelectionDialog = ({
  open,
  onClose,
  onSelect,
  existingProjectUuids,
}: ProjectSelectionDialogProps) => {
  const [allProjects, setAllProjects] = useState<ProjectForSelection[]>([])
  const [searchText, setSearchText] = useState('')
  const [selectedUuids, setSelectedUuids] = useState<Set<string>>(new Set())
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return
    const load = async () => {
      setLoading(true)
      try {
        const projects = await fetchAllProjects()
        setAllProjects(projects)
      } finally {
        setLoading(false)
      }
    }
    load()
    setSearchText('')
    setSelectedUuids(new Set())
  }, [open])

  const existingUuidSet = useMemo(
    () => new Set(existingProjectUuids),
    [existingProjectUuids]
  )

  const filteredProjects = useMemo(() => {
    const available = allProjects.filter(p => !existingUuidSet.has(p.uuid))
    if (!searchText) return available
    const lower = searchText.toLowerCase()
    return available.filter(
      p =>
        p.displayName.toLowerCase().includes(lower) ||
        p.code.toLowerCase().includes(lower)
    )
  }, [allProjects, existingUuidSet, searchText])

  const handleToggle = useCallback((uuid: string) => {
    setSelectedUuids(prev => {
      const next = new Set(prev)
      if (next.has(uuid)) {
        next.delete(uuid)
      } else {
        next.add(uuid)
      }
      return next
    })
  }, [])

  const handleAdd = useCallback(() => {
    const selected = allProjects.filter(p => selectedUuids.has(p.uuid))
    onSelect(selected)
    onClose()
  }, [allProjects, selectedUuids, onSelect, onClose])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth={true}>
      <DialogTitle>
        {intl.formatMessage({
          id: 'programManagement.projectSelection.title',
        })}
      </DialogTitle>
      <DialogContent dividers={true}>
        <SearchArea>
          <TextField
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'programManagement.projectSelection.search',
            })}
            size="small"
            fullWidth={true}
          />
        </SearchArea>
        {filteredProjects.length === 0 && !loading ? (
          <NoProjectsText>
            {intl.formatMessage({
              id: 'programManagement.projectSelection.noProjects',
            })}
          </NoProjectsText>
        ) : (
          <ProjectList>
            {filteredProjects.map(project => (
              <ListItem key={project.uuid} disablePadding={true}>
                <ListItemButton
                  onClick={() => handleToggle(project.uuid)}
                  dense={true}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={selectedUuids.has(project.uuid)}
                      tabIndex={-1}
                      disableRipple={true}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={project.displayName}
                    secondary={project.code}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </ProjectList>
        )}
      </DialogContent>
      <DialogActions>
        <DialogButton
          onClick={handleAdd}
          color="primary"
          disabled={selectedUuids.size === 0}
        >
          {intl.formatMessage({
            id: 'programManagement.projectSelection.add',
          })}
        </DialogButton>
        <DialogButton onClick={onClose} color="primary">
          {intl.formatMessage({
            id: 'programManagement.projectSelection.cancel',
          })}
        </DialogButton>
      </DialogActions>
    </Dialog>
  )
}

const SearchArea = styled('div')({
  marginBottom: '12px',
})

const ProjectList = styled(List)({
  height: '400px',
  overflow: 'auto',
  border: `1px solid ${colorPalette.monotone[3]}`,
  borderRadius: '4px',
})

const NoProjectsText = styled('div')({
  padding: '16px',
  textAlign: 'center',
  color: colorPalette.monotone[5],
})
